import React, { useState, useEffect } from 'react';
import { Card, Switch, Tag, Space, Spin, Alert, Typography, Row, Col, Statistic } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { 
  SourceCodeData, 
  CoverageData, 
  MergedLineData
} from '../types';
import './SourceCodeViewer.css';

const { Text, Title } = Typography;

interface SourceCodeViewerProps {
  projectId: string;
  commitHash: string;
  filePath: string;
}

const SourceCodeViewer: React.FC<SourceCodeViewerProps> = ({
  projectId,
  commitHash,
  filePath
}: SourceCodeViewerProps) => {
  const [mergedData, setMergedData] = useState<MergedLineData[]>([]);
  const [sourceData, setSourceData] = useState<SourceCodeData | null>(null);
  const [coverageData, setCoverageData] = useState<CoverageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTestTypes, setSelectedTestTypes] = useState<string[]>(['ut', 'at']);

  useEffect(() => {
    fetchFileData();
  }, [projectId, commitHash, filePath]);

  const fetchFileData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [sourceResponse, coverageResponse] = await Promise.all([
        fetch(`https://mini.easy2hub.com/cov/api/source/${projectId}/${commitHash}/${filePath}`),
        fetch(`https://mini.easy2hub.com/cov/api/coverage/${projectId}/${commitHash}/${filePath}`)
      ]);

      if (!sourceResponse.ok) {
        throw new Error(`Source API error: ${sourceResponse.status}`);
      }
      if (!coverageResponse.ok) {
        throw new Error(`Coverage API error: ${coverageResponse.status}`);
      }

      const source: SourceCodeData = await sourceResponse.json();
      const coverage: CoverageData = await coverageResponse.json();

      setSourceData(source);
      setCoverageData(coverage);

      // 合并源码和覆盖率数据
      const merged = mergeSourceAndCoverage(source, coverage);
      setMergedData(merged);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch file data');
    } finally {
      setLoading(false);
    }
  };

  const mergeSourceAndCoverage = (source: SourceCodeData, coverage: CoverageData): MergedLineData[] => {
    const merged = source.lines.map(line => {
      const lineCoverage = coverage.lineCoverage[line.lineNumber.toString()];
      return {
        ...line,
        coverage: lineCoverage?.coverage
      };
    });
    
    return merged;
  };

  const getLineCoverageColor = (line: MergedLineData): string => {
    if (!line.coverage || line.isEmpty || line.isComment) return 'transparent';
    
    const utSelected = selectedTestTypes.includes('ut');
    const atSelected = selectedTestTypes.includes('at');
    
    const utCoverageData = line.coverage?.ut;
    const atCoverageData = line.coverage?.at;
    
    const utCovered = utSelected && utCoverageData && utCoverageData.covered === true;
    const atCovered = atSelected && atCoverageData && atCoverageData.covered === true;
    
    if (utCovered && atCovered) {
      return 'rgba(128, 90, 213, 0.25)';  // 紫色，表示两种测试类型都覆盖
    } else if (utCovered && !atCovered) {
      return 'rgba(24, 144, 255, 0.25)';  // 蓝色，只被UT覆盖
    } else if (!utCovered && atCovered) {
      return 'rgba(82, 196, 26, 0.25)';   // 绿色，只被AT覆盖
    } else {
      return 'rgba(255, 77, 79, 0.25)';   // 红色，未被覆盖
    }
  };

  const getCoverageTypeColor = (coverageType: string): string => {
    switch (coverageType) {
      case 'FC': return '#52c41a'; // Full Coverage
      case 'PC': return '#faad14'; // Partial Coverage
      case 'NC': return '#ff4d4f'; // No Coverage
      default: return '#d9d9d9';
    }
  };

  const getTestTypeColor = (testType: string): string => {
    switch (testType) {
      case 'ut': return '#1890ff';
      case 'at': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  if (loading) {
    return (
      <div className="source-code-loading">
        <Spin size="large" tip="Loading source code and coverage data..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <button onClick={fetchFileData}>
            Retry
          </button>
        }
      />
    );
  }

  if (!sourceData || !coverageData) {
    return <div>No data available</div>;
  }

  return (
    <div className="source-code-viewer">
      {/* File Header */}
      <Card size="small" className="file-header">
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={4} style={{ margin: 0, color: '#cccccc' }}>
              {sourceData.fileName}
            </Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {sourceData.filePath}
            </Text>
          </Col>
          <Col>
            <Space>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {sourceData.totalLines} lines • {sourceData.codeLines} code • {sourceData.commentLines} comments
              </Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Coverage Statistics */}
      <Card size="small" className="coverage-stats">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Overall Coverage"
              value={coverageData.coverageStats.overall.coverageRate}
              suffix="%"
              valueStyle={{ 
                color: getCoverageTypeColor(coverageData.coverageStats.overall.coverageRate >= 80 ? 'FC' : 
                                          coverageData.coverageStats.overall.coverageRate >= 60 ? 'PC' : 'NC')
              }}
            />
          </Col>
          {Object.entries(coverageData.coverageStats.byTestType).map(([testType, stats]) => (
            <Col span={6} key={testType}>
              <Statistic
                title={`${testType.toUpperCase()} Coverage`}
                value={stats.coverageRate}
                suffix="%"
                valueStyle={{ 
                  color: getCoverageTypeColor(stats.coverageType)
                }}
              />
            </Col>
          ))}
        </Row>
      </Card>

      {/* Test Type Filter */}
      <Card size="small" className="test-type-filter">
        <Space>
          <Text strong style={{ color: '#cccccc' }}>Show Coverage:</Text>
          {['ut', 'at'].map(type => (
            <label key={type} className="test-type-switch">
              <Switch
                size="small"
                checked={selectedTestTypes.includes(type)}
                onChange={(checked) => {
                  if (checked) {
                    setSelectedTestTypes([...selectedTestTypes, type]);
                  } else {
                    setSelectedTestTypes(selectedTestTypes.filter(t => t !== type));
                  }
                }}
              />
              <Tag 
                color={getTestTypeColor(type)} 
                style={{ marginLeft: 8, margin: 0 }}
              >
                {type.toUpperCase()}
              </Tag>
            </label>
          ))}
        </Space>
      </Card>

      {/* Source Code */}
      <Card size="small" className="source-code-card">
        <div className="source-lines">
          {mergedData.map(line => (
            <div
              key={`${line.lineNumber}-${selectedTestTypes.join('-')}`}
              className={`source-line ${line.isEmpty ? 'empty-line' : ''} ${line.isComment ? 'comment-line' : ''}`}
              style={{ backgroundColor: getLineCoverageColor(line) }}
            >
              <div className="line-number">
                {line.lineNumber}
              </div>
              <div className="line-content">
                <code>{line.content}</code>
              </div>
              {line.coverage && (
                <div className="coverage-indicators">
                  {selectedTestTypes.map(type => {
                    const coverage = line.coverage?.[type as keyof typeof line.coverage];
                    if (!coverage) return null;
                    
                    return (
                      <Tag
                        key={type}
                        color={coverage.covered ? getTestTypeColor(type) : 'default'}
                        icon={coverage.covered ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        className="coverage-tag"
                        title={`${type.toUpperCase()}: ${coverage.covered ? 'Covered' : 'Not Covered'} (${coverage.coverageType})`}
                      >
                        {type.toUpperCase()}
                      </Tag>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default SourceCodeViewer;
